import React, { useMemo } from "react";
import { Box, Card, CardContent, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardGroupedChartHint from "./DashboardGroupedChartHint";
import { truncateChartLabel } from "../../utils/chartData";

const BR = (value) => Number(value || 0).toLocaleString("pt-BR");
const BRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const WHATSAPP_COLOR = "#25D366";
const WEBCHAT_COLOR = "#0F6E56";

function channelLabel(row, t) {
  if (row?.isOthers || row?.name === "__others__") return t("analytics.channelOthers");
  return String(row?.name || "");
}

function TipCard({ children }) {
  return (
    <Box sx={{ backgroundColor: "#fff", p: 1.2, borderRadius: 1, boxShadow: "0 0 10px rgba(0,0,0,.2)" }}>
      {children}
    </Box>
  );
}

function ChannelVolumeTooltip({ active, payload, hasSales, salesMode, mode = "volume", t }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const money = salesMode !== "orders";
  const formatSales = (value) => (money ? BRL(value) : BR(value));
  const lines = mode === "sales"
    ? [
        [t("analytics.seriesWhatsapp"), money ? row.salesWhatsapp : row.ordersWhatsapp],
        [t("analytics.seriesWebchat"), money ? row.salesWebchat : row.ordersWebchat],
        [t("analytics.seriesOtherChannel"), money ? row.salesOther : row.ordersOther],
      ].filter(([, value]) => Number(value) > 0)
    : [
        row.whatsapp > 0 ? [t("analytics.seriesWhatsapp"), row.whatsapp] : null,
        row.webchat > 0 ? [t("analytics.seriesWebchat"), row.webchat] : null,
        row.other > 0 ? [t("analytics.seriesOtherChannel"), row.other] : null,
      ].filter(Boolean);
  return (
    <TipCard>
      <Typography variant="subtitle2" fontWeight="bold">{row.name}</Typography>
      {lines.map(([label, value]) => (
        <Typography key={label} variant="body2">
          {label}: {mode === "sales" ? formatSales(value) : BR(value)}
        </Typography>
      ))}
      {mode === "volume" ? (
        <Typography variant="body2">{t("analytics.seriesVolume")}: {BR(row.volume)}</Typography>
      ) : null}
      {hasSales && mode === "volume" ? (
        <Typography variant="body2">
          {t("analytics.seriesSales")}: {salesMode === "orders" ? BR(row.salesOrders) : BRL(row.salesRevenue)}
          {salesMode === "revenue" && row.salesOrders > 0 ? ` (${BR(row.salesOrders)})` : ""}
        </Typography>
      ) : null}
    </TipCard>
  );
}

function CompareChartFrame({ title, hint, loading, children, extra }) {
  return (
    <Card sx={{ overflow: "hidden", height: "100%" }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {hint}
        </Typography>
        {extra}
        {loading ? <Skeleton variant="rectangular" height={300} /> : children}
      </CardContent>
    </Card>
  );
}

function useChannelChartData(channelCompare, t) {
  return useMemo(() => {
    const rows = (channelCompare?.rows || [])
      .map((row) => ({ ...row, name: channelLabel(row, t) }))
      .filter((row) => row.volume > 0 || row.salesOrders > 0 || row.salesRevenue > 0);
    const hasWebchat = rows.some((row) => row.webchat > 0 || row.salesWebchat > 0 || row.ordersWebchat > 0);
    const hasWhatsapp = rows.some((row) => row.whatsapp > 0 || row.salesWhatsapp > 0 || row.ordersWhatsapp > 0);
    const hasOther = rows.some((row) => row.other > 0 || row.salesOther > 0 || row.ordersOther > 0);
    const salesMode = rows.some((row) => row.salesRevenue > 0)
      ? "revenue"
      : rows.some((row) => row.salesOrders > 0)
        ? "orders"
        : null;
    return { rows, hasWebchat, hasWhatsapp, hasOther, salesMode };
  }, [channelCompare, t]);
}

function StackedChannelChart({ rows, series, valueFormatter, tooltip, horizontal }) {
  const height = horizontal ? Math.min(520, Math.max(280, rows.length * 36)) : 300;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={rows}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ left: 4, right: 16, top: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        {horizontal ? (
          <>
            <XAxis type="number" tickFormatter={valueFormatter} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={148}
              tick={{ fontSize: 11 }}
              tickFormatter={(label) => truncateChartLabel(label, 22)}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              interval={0}
              tickFormatter={(label) => truncateChartLabel(label, 16)}
              height={56}
            />
            <YAxis tickFormatter={valueFormatter} allowDecimals={false} width={72} />
          </>
        )}
        <Tooltip content={tooltip} />
        <Legend />
        {series.map((item) => (
          <Bar key={item.key} dataKey={item.key} name={item.name} stackId="canal" fill={item.fill} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function DashboardChannelCompareSection({
  loading = false,
  channelCompare = null,
  handlerCompare = null,
}) {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const channels = useChannelChartData(channelCompare, t);
  const salesMode = channels.salesMode;
  const hasSales = Boolean(channelCompare?.hasSales || handlerCompare?.hasSales || salesMode);

  const handlerRows = useMemo(() => {
    const order = ["humanos", "chatbots", "agentes", "fila"];
    const labels = {
      humanos: t("analytics.handlerHuman"),
      chatbots: t("analytics.handlerBot"),
      agentes: t("analytics.handlerAgent"),
      fila: t("analytics.handlerQueue"),
    };
    return order
      .map((key) => {
        const row = (handlerCompare?.rows || []).find((item) => item.key === key) || {};
        return {
          key,
          name: labels[key],
          volume: Number(row.volume || 0),
          salesOrders: Number(row.salesOrders || 0),
          salesRevenue: Number(row.salesRevenue || 0),
          salesValue: salesMode === "orders" ? Number(row.salesOrders || 0) : Number(row.salesRevenue || 0),
        };
      })
      .filter((row) => row.key !== "fila" || row.volume > 0 || row.salesOrders > 0 || row.salesRevenue > 0);
  }, [handlerCompare, salesMode, t]);

  const horizontal = channels.rows.length > 7;
  const volumeSeries = [
    channels.hasWhatsapp
      ? { key: "whatsapp", name: t("analytics.seriesWhatsapp"), fill: WHATSAPP_COLOR }
      : null,
    channels.hasWebchat
      ? { key: "webchat", name: t("analytics.seriesWebchat"), fill: WEBCHAT_COLOR }
      : null,
    channels.hasOther
      ? { key: "other", name: t("analytics.seriesOtherChannel"), fill: theme.palette.info.main }
      : null,
  ].filter(Boolean);

  const salesSeries = [
    channels.hasWhatsapp
      ? {
          key: salesMode === "orders" ? "ordersWhatsapp" : "salesWhatsapp",
          name: t("analytics.seriesWhatsapp"),
          fill: WHATSAPP_COLOR,
        }
      : null,
    channels.hasWebchat
      ? {
          key: salesMode === "orders" ? "ordersWebchat" : "salesWebchat",
          name: t("analytics.seriesWebchat"),
          fill: WEBCHAT_COLOR,
        }
      : null,
    channels.hasOther
      ? {
          key: salesMode === "orders" ? "ordersOther" : "salesOther",
          name: t("analytics.seriesOtherChannel"),
          fill: theme.palette.info.main,
        }
      : null,
  ].filter(Boolean);

  const empty = !loading && channels.rows.length === 0;
  if (!loading && !channelCompare && !handlerCompare) return null;

  return (
    <Grid container spacing={3} sx={{ mb: 2 }}>
      <Grid item xs={12} md={7}>
        <CompareChartFrame
          title={t("analytics.channelCompareTitle")}
          hint={t("analytics.channelCompareHint")}
          loading={loading}
          extra={
            !loading && channelCompare?.grouped ? (
              <DashboardGroupedChartHint
                shown={channels.rows.length}
                total={channelCompare.totalCount}
              />
            ) : null
          }
        >
          {empty ? (
            <Typography variant="body2" color="text.secondary">{t("analytics.channelCompareEmpty")}</Typography>
          ) : (
            <StackedChannelChart
              rows={channels.rows}
              series={volumeSeries}
              valueFormatter={(value) => BR(value)}
              horizontal={horizontal}
              tooltip={(props) => (
                <ChannelVolumeTooltip {...props} hasSales={hasSales} salesMode={salesMode} t={t} />
              )}
            />
          )}
        </CompareChartFrame>
      </Grid>

      <Grid item xs={12} md={5}>
        <CompareChartFrame
          title={t("analytics.handlerCompareTitle")}
          hint={t("analytics.handlerCompareHint")}
          loading={loading}
        >
          {empty ? (
            <Typography variant="body2" color="text.secondary">{t("analytics.channelCompareEmpty")}</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={handlerRows} margin={{ left: 4, right: hasSales ? 16 : 8, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-18}
                  textAnchor="end"
                  height={64}
                />
                <YAxis yAxisId="volume" allowDecimals={false} width={48} />
                {hasSales ? (
                  <YAxis
                    yAxisId="sales"
                    orientation="right"
                    width={64}
                    tickFormatter={(value) => (salesMode === "orders" ? BR(value) : BRL(value))}
                  />
                ) : null}
                <Tooltip
                  formatter={(value, name) => {
                    if (name === t("analytics.seriesSales")) {
                      return salesMode === "orders" ? BR(value) : BRL(value);
                    }
                    return BR(value);
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="volume"
                  dataKey="volume"
                  name={t("analytics.seriesVolume")}
                  fill={theme.palette.primary.main}
                />
                {hasSales ? (
                  <Bar
                    yAxisId="sales"
                    dataKey="salesValue"
                    name={t("analytics.seriesSales")}
                    fill={theme.palette.success.main}
                  />
                ) : null}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CompareChartFrame>
      </Grid>

      {!loading && hasSales && salesSeries.length > 0 ? (
        <Grid item xs={12}>
          <CompareChartFrame
            title={t("analytics.channelSalesTitle")}
            hint={
              salesMode === "orders"
                ? t("analytics.channelSalesOrdersHint")
                : t("analytics.channelSalesHint")
            }
            loading={false}
          >
            <StackedChannelChart
              rows={channels.rows}
              series={salesSeries}
              horizontal={horizontal}
              valueFormatter={(value) => (salesMode === "orders" ? BR(value) : BRL(value))}
              tooltip={(props) => (
                <ChannelVolumeTooltip {...props} hasSales salesMode={salesMode} mode="sales" t={t} />
              )}
            />
          </CompareChartFrame>
        </Grid>
      ) : null}
    </Grid>
  );
}
